import { useState } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import './AIQuizGenerator.css';

// サーバー側（functions/index.js）の入力制限と揃える
const MAX_TEXT_LENGTH = 20000;
const MAX_IMAGE_SIZE = 7 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif'];

const AIQuizGenerator = ({ onGenerate, onCancel }) => {
  const { user } = useAuth();
  const [textInput, setTextInput] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationOptions, setGenerationOptions] = useState({
    questionCount: 5,
    difficulty: 'medium',
    questionType: 'mixed'
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        alert('PNG / JPEG / WebP / HEIC / HEIF 形式の画像ファイルを選択してください');
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        alert('画像サイズは7MB以下にしてください');
        return;
      }

      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    // Reset file input
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleGenerate = async () => {
    // ユーザー認証チェック
    if (!user) {
      alert('ログインしてください');
      return;
    }

    // Validate input - at least text or image is required
    if (!textInput.trim() && !selectedImage) {
      alert('テキストまたは画像を入力してください');
      return;
    }

    setIsGenerating(true);

    try {
      // Firebase JWTトークンを取得
      const token = await user.getIdToken();
      console.log('AIクイズ生成リクエスト開始:', { 
        user: user.uid, 
        method: 'combined',
        hasToken: !!token 
      });

      // Prepare data for AI generation
      const generationData = {
        method: selectedImage ? (textInput.trim() ? 'combined' : 'image') : 'text',
        content: textInput,
        options: generationOptions
      };

      // Convert image to base64 if selected
      if (selectedImage) {
        const base64Image = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:image/jpeg;base64, prefix
          reader.onerror = reject;
          reader.readAsDataURL(selectedImage);
        });
        generationData.imageData = base64Image;
        generationData.imageType = selectedImage.type;
      }

      // Call AI function with JWT authentication
      const response = await fetch('https://asia-northeast1-quizenie-5cf54.cloudfunctions.net/generateAIQuiz', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(generationData)
      });
      
      const result = await response.json();
      
      console.log('AIクイズ生成レスポンス:', { 
        status: response.status, 
        success: result.success 
      });

      if (!response.ok || !result.success) {
        throw new Error(result.message || `HTTP ${response.status}: APIエラーが発生しました`);
      }
      
      // Pass the generated quiz data to parent component
      onGenerate(result.quiz_data || result);
    } catch (error) {
      console.error('AI quiz generation failed:', error);
      
      // より詳細なエラーメッセージを表示
      let errorMessage = 'クイズ生成に失敗しました';
      if (error.message.includes('401')) {
        errorMessage = '認証エラー: ログインし直してください';
      } else if (error.message.includes('500')) {
        errorMessage = 'サーバーエラー: しばらく後でお試しください';
      } else if (error.message) {
        errorMessage = `エラー: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="ai-quiz-generator">
      <div className="ai-quiz-generator-header">
        <h2>🤖 AIでクイズ作成</h2>
        <button onClick={onCancel} className="cancel-btn">
          ← 戻る
        </button>
      </div>

      <div className="ai-generator-content">
        <div className="input-section">
          <div className="combined-input-section">
            <h3>テキストと画像からクイズを生成</h3>
            <p>テキストと画像を組み合わせて、より詳細なクイズを作成できます。</p>
            
            <div className="text-section">
              <h4>📝 テキスト入力</h4>
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="クイズにしたい内容を入力してください。画像と関連する説明や背景情報も含めると、より良いクイズが生成されます。"
                className="text-input"
                rows="6"
                maxLength={MAX_TEXT_LENGTH}
              />
            </div>

            <div className="image-section">
              <h4>🖼️ 画像アップロード（オプション）</h4>
              <div className="image-upload-area">
                <input
                  type="file"
                  accept={ALLOWED_IMAGE_TYPES.join(',')}
                  onChange={handleImageChange}
                  className="image-input"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="image-upload-label">
                  {selectedImage ? '画像を変更' : '📁 画像を選択'}
                </label>
                {selectedImage && (
                  <button type="button" onClick={clearImage} className="clear-image-btn">
                    ❌ 画像を削除
                  </button>
                )}
              </div>
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="アップロード画像" />
                  <div className="image-info">
                    <p>ファイル名: {selectedImage?.name}</p>
                    <p>サイズ: {(selectedImage?.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              )}
            </div>

            <div className="combined-note">
              <small>💡 テキストのみ、画像のみ、または両方を組み合わせてクイズを生成できます</small>
            </div>
          </div>
        </div>

        <div className="generation-options">
          <h3>生成オプション</h3>
          <div className="options-grid">
            <div className="option-group">
              <label>問題数</label>
              <select
                value={generationOptions.questionCount}
                onChange={(e) => setGenerationOptions({
                  ...generationOptions,
                  questionCount: parseInt(e.target.value)
                })}
              >
                <option value={3}>3問</option>
                <option value={5}>5問</option>
                <option value={10}>10問</option>
                <option value={15}>15問</option>
                <option value={20}>20問</option>
              </select>
            </div>

            <div className="option-group">
              <label>難易度</label>
              <select
                value={generationOptions.difficulty}
                onChange={(e) => setGenerationOptions({
                  ...generationOptions,
                  difficulty: e.target.value
                })}
              >
                <option value="easy">簡単</option>
                <option value="medium">普通</option>
                <option value="hard">難しい</option>
              </select>
            </div>

            <div className="option-group">
              <label>問題形式</label>
              <select
                value={generationOptions.questionType}
                onChange={(e) => setGenerationOptions({
                  ...generationOptions,
                  questionType: e.target.value
                })}
              >
                <option value="mixed">混合</option>
                <option value="single">単一選択のみ</option>
                <option value="multiple">複数選択のみ</option>
              </select>
            </div>
          </div>
        </div>

        <div className="generation-actions">
          <button onClick={onCancel} className="cancel-btn">
            キャンセル
          </button>
          <button 
            onClick={handleGenerate} 
            className="generate-btn"
            disabled={isGenerating}
          >
            {isGenerating ? '🤖 生成中...' : '🚀 クイズを生成'}
          </button>
        </div>
      </div>
    </div>
  );
};
export default AIQuizGenerator;
